import React, { useState } from 'react';
import { FileText, Image, ZoomIn, X, AlertTriangle } from 'lucide-react';
import { PersonDetailData } from '../index';
import { cn } from '../../../utils/cn';
import { api } from '../../../services/api';
import toast from 'react-hot-toast';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { documentSlots, DocumentPhotoType, DocumentSlot } from './documentSlots';

interface DocumentsTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  /** Open the modal's shared photo viewer on this URL — the same one the sidebar opens. */
  onPhotoClick: (url: string) => void;
  /** Re-capture the photo in place, replacing `photoId` when there is one. */
  onEditPhoto: (photo: { photoId?: string; url: string; photoType: DocumentPhotoType }) => void;
  /** Capture a photo for a slot from scratch. */
  onNewPhoto: (photoType: DocumentPhotoType) => void;
  /**
   * Edit every photo side by side. Omitted by hosts that do not offer it — the tab renders
   * no button rather than a dead one. Like the single-photo flows above, the tab only reports
   * the press: PersonDetailModal opens the same AllPhotosEditModal the registration screens
   * use, which is what keeps the two from drifting apart.
   */
  onEditAllPhotos?: () => void;
  readOnly?: boolean;
  darkMode?: boolean;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  person,
  onRefresh,
  onPhotoClick,
  onEditPhoto,
  onNewPhoto,
  onEditAllPhotos,
  readOnly,
  darkMode = false,
}) => {
  const [viewingImage, setViewingImage] = useState<{ url: string; title: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  // "Fora do padrão" flow for documents
  const [flaggedDocType, setFlaggedDocType] = useState<'document_front' | 'document_back' | 'vehicle' | null>(null);
  const [showFlagConfirmModal, setShowFlagConfirmModal] = useState(false);
  const openDocFlagFlow = (docType: 'document_front' | 'document_back' | 'vehicle') => {
    setFlaggedDocType(docType);
    setShowFlagConfirmModal(true);
  };

  const flagImageFor = (docType: 'document_front' | 'document_back' | 'vehicle' | null) =>
    docType === 'vehicle' ? '/veiculo-fora-padrao.jpeg' : '/documento-fora-padrao.jpeg';

  const confirmDocFlagFlow = async () => {
    if (!flaggedDocType || !person.customer?.id) return;
    setShowFlagConfirmModal(false);
    setUploading(true);
    try {
      const imgResponse = await fetch(flagImageFor(flaggedDocType));
      const blob = await imgResponse.blob();

      const formData = new FormData();
      formData.append('photo', blob, 'foto-fora-padrao.jpg');
      formData.append('customer_id', person.customer.id);
      formData.append('photo_type', flaggedDocType);

      await api.post(`/people/${person.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Documento marcado como fora do padrão!');
      setFlaggedDocType(null);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to upload flag document:', err);
      toast.error(err.response?.data?.error || 'Erro ao marcar documento');
    } finally {
      setUploading(false);
    }
  };

  const cancelDocFlagFlow = () => {
    setShowFlagConfirmModal(false);
    setFlaggedDocType(null);
  };

  const slots = documentSlots(person);

  const openImageViewer = (url: string, title: string) => {
    setViewingImage({ url, title });
  };

  const closeImageViewer = () => {
    setViewingImage(null);
  };

  return (
    <div className="space-y-4">
      {onEditAllPhotos && !readOnly && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onEditAllPhotos}
            className={cn(
              'text-xs font-medium px-3 py-1.5 rounded-md transition-colors',
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
            )}
          >
            Editar todas
          </button>
        </div>
      )}

      {/* Document Info */}
      {(person.document_type || person.document_number) && (
        <div className={cn(
          "rounded-lg p-4 border",
          darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
        )}>
          <h4 className={cn(
            "text-sm font-medium mb-2 flex items-center gap-2",
            darkMode ? "text-gray-300" : "text-gray-700"
          )}>
            <FileText className="w-4 h-4" />
            Informações do Documento
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {person.document_type && (
              <div>
                <p className={cn(
                  "text-xs",
                  darkMode ? "text-gray-400" : "text-gray-500"
                )}>
                  Tipo
                </p>
                <p className={cn(
                  "text-sm font-medium",
                  darkMode ? "text-gray-100" : "text-gray-900"
                )}>
                  {person.document_type}
                </p>
              </div>
            )}
            {person.document_number && (
              <div>
                <p className={cn(
                  "text-xs",
                  darkMode ? "text-gray-400" : "text-gray-500"
                )}>
                  Número
                </p>
                <p className={cn(
                  "text-sm font-medium",
                  darkMode ? "text-gray-100" : "text-gray-900"
                )}>
                  {person.document_number}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Images */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {slots.map((slot) => (
          <DocumentSlotPanel
            key={slot.type}
            slot={slot}
            readOnly={readOnly}
            darkMode={darkMode}
            onView={() => onPhotoClick(slot.url)}
            onUpdate={() => onEditPhoto({ photoId: slot.photoId, url: slot.url, photoType: slot.type })}
            onNew={() => onNewPhoto(slot.type)}
            onFlag={() => openDocFlagFlow(slot.type)}
          />
        ))}
      </div>

      {/* Outros Documentos */}
      {!readOnly && person.customer?.id && (
        <div className="mt-4">
          {/* Other Documents */}
          <div className={cn(
            "rounded-lg border overflow-hidden",
            darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          )}>
            <div className={cn(
              "px-3 py-2 border-b",
              darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            )}>
              <h4 className={cn(
                "text-sm font-medium",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Outros Documentos
              </h4>
            </div>
            <div className="p-3 space-y-2">
              {/* List of existing other docs */}
              {(() => {
                const otherDocs = person.identity_photos?.filter(p => p.photo_type === 'other') || [];
                return otherDocs.length > 0 ? (
                  <div className="space-y-2">
                    {otherDocs.map((doc, idx) => (
                      <div key={doc.id} className={cn(
                        "flex items-center gap-3 p-2 rounded-lg border",
                        darkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
                      )}>
                        <img
                          src={getAbsoluteImageUrl(doc.photo_url)}
                          alt={`Documento ${idx + 1}`}
                          className="w-16 h-12 object-cover rounded cursor-pointer flex-shrink-0"
                          onClick={() => openImageViewer(getAbsoluteImageUrl(doc.photo_url), `Documento ${idx + 1}`)}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={cn("text-xs font-medium", darkMode ? "text-gray-300" : "text-gray-700")}>
                            Documento {idx + 1}
                          </p>
                          <p className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                            {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <button
                          onClick={() => openImageViewer(getAbsoluteImageUrl(doc.photo_url), `Documento ${idx + 1}`)}
                          className={cn("p-1.5 rounded transition-colors flex-shrink-0", darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-700")}
                        >
                          <ZoomIn className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={cn("text-xs text-center py-2", darkMode ? "text-gray-500" : "text-gray-400")}>
                    Nenhum documento adicional
                  </p>
                );
              })()}

              <button
                type="button"
                onClick={() => onNewPhoto('other')}
                className={cn(
                  "w-full px-3 py-1.5 text-xs font-bold tracking-wide rounded-lg border transition-colors",
                  darkMode
                    ? "text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700"
                    : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                )}
              >
                ADICIONAR DOCUMENTO
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FORA DO PADRÃO — Confirmation Modal */}
      {showFlagConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={cancelDocFlagFlow} />
          <div className={cn(
            "relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden",
            darkMode ? "bg-gray-900" : "bg-white"
          )}>
            <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-200 bg-amber-50">
              <div className="p-2 bg-amber-100 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-amber-900 tracking-wide">
                  {flaggedDocType === 'vehicle' ? 'VEÍCULO FORA DO PADRÃO' : 'DOCUMENTO FORA DO PADRÃO'}
                </h3>
                <p className="text-xs text-amber-700">
                  {flaggedDocType === 'document_front' ? 'Frente do documento' : flaggedDocType === 'document_back' ? 'Verso do documento' : 'Foto do veículo (placa)'}
                </p>
              </div>
              <button onClick={cancelDocFlagFlow} className="ml-auto p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className={cn("text-sm", darkMode ? "text-gray-300" : "text-gray-700")}>
                Deseja registrar um novo documento agora?
              </p>
              <img
                src={flagImageFor(flaggedDocType)}
                alt="Padrão de foto"
                className="w-full rounded-xl object-contain"
              />
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={cancelDocFlagFlow}
                className={cn(
                  "flex-1 py-2.5 text-sm font-medium rounded-xl border transition-colors",
                  darkMode ? "text-gray-300 border-gray-600 hover:bg-gray-800" : "text-gray-700 border-gray-300 hover:bg-gray-50"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDocFlagFlow}
                className="flex-1 py-2.5 text-sm font-bold tracking-wide text-white bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors"
              >
                CONFIRMAR
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Fullscreen Image Viewer */}
      {viewingImage && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90"
          onClick={closeImageViewer}
        >
          <button
            onClick={closeImageViewer}
            className="absolute top-4 right-4 z-10 p-3 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-8 h-8" />
          </button>

          <div
            className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={viewingImage.url}
              alt={viewingImage.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-semibold">{viewingImage.title}</p>
              <p className="text-white/60 text-sm mt-1">
                {person.first_name} {person.last_name}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DocumentSlotPanelProps {
  slot: DocumentSlot;
  readOnly?: boolean;
  darkMode: boolean;
  /** Open the photo in the modal's shared viewer. */
  onView: () => void;
  /** Re-capture over the photo in place. */
  onUpdate: () => void;
  /** Capture a photo for this slot from scratch. */
  onNew: () => void;
  onFlag: () => void;
}

/**
 * One document panel, carrying the same two actions over the photo as the lightbox does.
 *
 * The strip is always visible rather than appearing on hover: these are the only way to
 * replace a document from this tab, and an operator with a camera in their hand should not
 * have to discover them.
 */
const DocumentSlotPanel: React.FC<DocumentSlotPanelProps> = ({
  slot,
  readOnly,
  darkMode,
  onView,
  onUpdate,
  onNew,
  onFlag,
}) => (
  <div className={cn(
    "rounded-lg border overflow-hidden",
    darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
  )}>
    <div className={cn(
      "px-3 py-2 border-b",
      darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
    )}>
      <h4 className={cn(
        "text-sm font-medium",
        darkMode ? "text-gray-300" : "text-gray-700"
      )}>
        {slot.title}
      </h4>
    </div>
    <div className="p-3 space-y-2">
      {slot.url ? (
        <div className="relative group rounded overflow-hidden">
          <img
            src={slot.url}
            alt={slot.title}
            className="w-full aspect-[3/2] object-contain cursor-pointer"
            onClick={onView}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
            <ZoomIn className="w-8 h-8 text-white" />
          </div>
          {!readOnly && (
            <div className="absolute bottom-0 inset-x-0 flex">
              <button
                type="button"
                onClick={onUpdate}
                className="flex-1 py-2 text-xs font-bold tracking-widest text-white/90 bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors border-r border-white/10"
              >
                ATUALIZAR
              </button>
              <button
                type="button"
                onClick={onNew}
                className="flex-1 py-2 text-xs font-bold tracking-widest text-white/90 bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors"
              >
                NOVA FOTO
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className={cn(
          'w-full aspect-[3/2] rounded flex flex-col items-center justify-center gap-2 border-2 border-dashed',
          darkMode ? 'border-gray-700 bg-gray-800 text-gray-600' : 'border-gray-200 bg-gray-50 text-gray-400'
        )}>
          <Image className="w-6 h-6" />
          <span className="text-xs">{readOnly ? 'Não disponível' : 'Sem foto'}</span>
        </div>
      )}

      {!readOnly && !slot.url && (
        <button
          type="button"
          onClick={onNew}
          className={cn(
            "w-full px-3 py-1.5 text-xs font-bold tracking-wide rounded-lg border transition-colors",
            darkMode
              ? "text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700"
              : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
          )}
        >
          {slot.addLabel}
        </button>
      )}

      {!readOnly && slot.url && (
        <button
          type="button"
          onClick={onFlag}
          className="w-full px-3 py-1.5 text-xs font-bold tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
        >
          FORA DO PADRÃO
        </button>
      )}
    </div>
  </div>
);
